import React, { useRef, useEffect, useMemo } from "react";
import { GoogleMapsOverlay } from "@deck.gl/google-maps";
import { HeatmapLayer } from "@deck.gl/aggregation-layers";

// Default heatmap color range
const DEFAULT_COLOR_RANGE = [
    [1, 152, 189],
    [73, 227, 206],
    [216, 254, 181],
    [254, 237, 177],
    [254, 173, 84],
    [209, 55, 78],
];

/**
 * Reusable DeckGL Heatmap component rendered on top of Google Maps.
 *
 * @param {Object} props
 * @param {Array} props.data - Array of data points with { latitude, longitude, weight } format
 * @param {Object} props.initialViewState - Initial map center/zoom { latitude, longitude, zoom }
 * @param {string} props.apiKey - Google Maps API key
 * @param {number} [props.intensity=1] - Heatmap intensity multiplier
 * @param {number} [props.radiusPixels=30] - Heatmap radius in pixels
 * @param {number} [props.threshold=0.03] - Heatmap threshold (0-1)
 * @param {Array} [props.colorRange] - Array of [r,g,b] colors for the heatmap gradient
 * @param {string} [props.id] - Layer id
 * @param {string} [props.mapId] - Google Maps Map ID (for cloud styling)
 * @param {Object} [props.style] - Container CSS style override
 * @param {Function} [props.getPosition] - Custom position accessor (default: d => [d.longitude, d.latitude])
 * @param {Function} [props.getWeight] - Custom weight accessor (default: d => d.weight || 1)
 */
export const DeckGLHeatmap = ({
    data = [],
    initialViewState,
    apiKey,
    intensity = 1,
    radiusPixels = 30,
    threshold = 0.03,
    colorRange = DEFAULT_COLOR_RANGE,
    id = "heatmap-layer",
    mapId,
    style,
    getPosition,
    getWeight,
}) => {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const overlayRef = useRef(null);

    const positionAccessor = useMemo(
        () => getPosition || (d => [d.longitude, d.latitude]),
        [getPosition]
    );
    const weightAccessor = useMemo(
        () => getWeight || (d => d.weight || 1),
        [getWeight]
    );

    // Initialize Google Map
    useEffect(() => {
        if (!containerRef.current || !window.google?.maps) return;

        const map = new window.google.maps.Map(containerRef.current, {
            center: {
                lat: initialViewState?.latitude ?? 41.15,
                lng: initialViewState?.longitude ?? -8.61,
            },
            zoom: initialViewState?.zoom ?? 11,
            mapId: mapId || undefined,
            disableDefaultUI: true,
            zoomControl: false,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            scrollwheel: true,
            draggable: true,
        });

        const overlay = new GoogleMapsOverlay({});
        overlay.setMap(map);

        mapRef.current = map;
        overlayRef.current = overlay;

        return () => {
            overlay.setMap(null);
        };
    }, [apiKey, initialViewState?.latitude, initialViewState?.longitude, initialViewState?.zoom, mapId]);

    // Update deck.gl layers when data changes
    useEffect(() => {
        if (!overlayRef.current) return;

        if (!data || data.length === 0) {
            overlayRef.current.setProps({ layers: [] });
            return;
        }

        const heatmapLayer = new HeatmapLayer({
            id,
            data,
            getPosition: positionAccessor,
            getWeight: weightAccessor,
            intensity,
            radiusPixels,
            threshold,
            colorRange,
            aggregation: "SUM",
        });

        overlayRef.current.setProps({ layers: [heatmapLayer] });
    }, [data, intensity, radiusPixels, threshold, colorRange, id, positionAccessor, weightAccessor]);

    const containerStyle = {
        position: "relative",
        width: "100%",
        height: "100%",
        ...style,
    };

    return <div ref={containerRef} style={containerStyle} />;
};

export default DeckGLHeatmap;
